import type { JSX } from "react";

import { PlusOutlined } from "@ant-design/icons";
import { zodResolver } from "@hookform/resolvers/zod";
import { RHFStringInput } from "@impero-com/common/components/form/rhfFields/rhfStringInput";
import * as Cmp from "@impero-com/common/utils/comparison";
import { t } from "@impero-com/common/utils/i18n";
import { Button } from "@impero-com/ui";
import { useForm } from "react-hook-form";
import { z } from "zod";

import { Guid } from "utils/guid";

import { MAX_TAG_NAME_LENGTH } from "../tagManagementTable";

import { CascaderOption } from "./editableTagListPlus";
import * as theme from "./theme.css";
import { BaseTag } from "./types";

interface TagAddInputProps<Tag extends BaseTag> {
    categoryOption: CascaderOption | undefined;
    onCreateTag: (name: string, categoryId: string) => void;
    getTagCategoryId: (tag: Tag) => Guid;
    isTagActive: (tag: Tag) => boolean;
    allTags: Tag[];
}
const TagAddInput = <Tag extends BaseTag>({
    categoryOption,
    onCreateTag,
    getTagCategoryId,
    isTagActive,
    allTags,
}: TagAddInputProps<Tag>): JSX.Element | null => {
    const newTagSchema = z
        .string()
        .trim()
        .min(1, {
            message: t("EditableTagListPlus_NewTag_Validation_NotEmpty"),
        })
        .max(MAX_TAG_NAME_LENGTH, {
            message: t("EditableTagListPlus_NewTag_Validation_TooLong"),
        });

    const refinedNewTagSchema = newTagSchema.refine(
        (value) => {
            const tagNames = allTags
                .filter(
                    (tag) =>
                        isTagActive(tag) &&
                        getTagCategoryId(tag) === categoryOption?.value
                )
                .map((tag) => tag.name);

            return tagNames.every((tagName) => !Cmp.citextEq(tagName, value));
        },
        { error: t("EditableTagListPlus_NewTag_Validation_AlreadyExists") }
    );

    const newTagFormSchema = z.object({
        newTag: refinedNewTagSchema,
    });
    type NewTagForm = z.infer<typeof newTagFormSchema>;
    const { control, handleSubmit, reset } = useForm<NewTagForm>({
        mode: "onChange",
        defaultValues: {
            newTag: "",
        },
        resolver: zodResolver(newTagFormSchema),
    });

    if (!categoryOption) {
        return null;
    }

    const handleSubmitClick = handleSubmit((values) => {
        onCreateTag(values.newTag, categoryOption.value);
        reset();
    });

    const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
        if (e.code === "Enter") {
            handleSubmitClick();
        }
        e.currentTarget.focus(); // Prevents blur after submit
        e.stopPropagation(); // Prevents Enter from selecting/deselecting category
    };

    return (
        <form className={theme.tagAddContainer}>
            <RHFStringInput
                control={control}
                name="newTag"
                placeholder={t("EditableTagListPlus_NewTag_NewTagInCategory", {
                    category: categoryOption.label,
                })}
                onKeyDown={handleKeyDown}
                maxRows={1}
                schema={refinedNewTagSchema}
            />
            <Button onClick={() => void handleSubmitClick()} variant="primary">
                <PlusOutlined /> {t("EditableTagListPlus_NewTag_AddTag")}
            </Button>
        </form>
    );
};

export type { TagAddInputProps };
export { TagAddInput };
